import { useCallback, useEffect, useRef, useState } from "react";
import {
  App as AntdApp,
  Alert,
  Badge,
  Button,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CommentOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { ticketService } from "../../../services/api/ticketService";
import type { TicketListItem } from "../../../types/ticket";
import { TicketThread } from "../../../components/tickets/TicketThread";
import { RichTextEditor } from "../../../components/common/RichTextEditor";

const { Text } = Typography;

interface PlanningCommentsPanelProps {
  planningGroupId: number;
  /** Cuando llega por deep-link (email → ?ticket=<id>), este ticket
   *  se abre directamente y salta la lista. */
  initialTicketId?: number | null;
  /** Se dispara cuando cambian los threads (crear nuevo, reply) — el
   *  parent puede refrescar contadores en la card de planning. */
  onChanged?: () => void;
}

/**
 * Panel de "Comentarios" de una PlanningGroup.
 *
 * Muestra la lista de hilos (`/v1/tickets/?planning_group=<id>`),
 * permite crear uno nuevo (subject + body) y abrir cualquiera para
 * ver la conversación completa reutilizando `<TicketThread>`.
 *
 * Diseñado para embeberse en un Drawer desde `PlanningGroupShow`, pero
 * es agnóstico de contenedor.
 */
export const PlanningCommentsPanel: React.FC<PlanningCommentsPanelProps> = ({
  planningGroupId,
  initialTicketId,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [threads, setThreads] = useState<TicketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openTicketId, setOpenTicketId] = useState<number | null>(
    initialTicketId ?? null,
  );

  const [newModalOpen, setNewModalOpen] = useState(false);
  const [newForm] = Form.useForm<{ subject: string; body_html: string }>();
  const [creating, setCreating] = useState(false);

  // Guardamos onChanged en un ref para poder llamarlo desde dentro de
  // `reload` sin meterlo en la deps array. Si lo dejáramos en las
  // deps, la función inline que el padre pasa cambiaría de identidad
  // en cada render → reload nuevo → useEffect → refetch → onChanged
  // → padre re-renderiza → nuevo onChanged inline → loop infinito.
  // Recién visto en prod: 503 de nginx por rate-limit tras crear el
  // primer hilo.
  const onChangedRef = useRef(onChanged);
  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ticketService.listTickets({
        planning_group: planningGroupId,
        // Todos los hilos, no solo abiertos — el histórico también
        // interesa (nueva audit añadida hace 3 semanas queda como
        // registro aunque el hilo se cierre después).
        pageSize: 100,
        ordering: "-last_activity",
      });
      setThreads(r.data ?? []);
      onChangedRef.current?.();
    } catch {
      notify.error(
        t(
          "planning.comments.load_error",
          "No se pudieron cargar los comentarios.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [planningGroupId, notify, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Callback estable para <TicketThread>: sin useCallback, cada render
  // del panel crearía una función nueva → TicketThread la incluye en
  // el ref, pero al refetch del thread llamaría a `reload` del panel
  // que actualiza state → panel re-renderiza. Con useCallback y sin
  // deps, la identidad es estable.
  const notifyPanelReload = useCallback(() => {
    void reload();
  }, [reload]);

  // Deep-link: si initialTicketId cambia por ?ticket=<id>, saltamos al thread.
  useEffect(() => {
    if (initialTicketId != null && initialTicketId !== openTicketId) {
      setOpenTicketId(initialTicketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTicketId]);

  const onCreate = async () => {
    const values = await newForm.validateFields().catch(() => null);
    if (!values) return;
    setCreating(true);
    try {
      const created = await ticketService.createTicket({
        subject: values.subject.trim(),
        body_html: values.body_html,
        planning_group: planningGroupId,
        // category la autoasigna el backend (kind='planning').
      });
      notify.success(t("planning.comments.created", "Hilo creado."));
      setNewModalOpen(false);
      newForm.resetFields();
      await reload();
      // Abrir directamente el nuevo hilo — es lo que el usuario
      // espera tras pulsar "Crear".
      setOpenTicketId(created.id);
    } catch {
      notify.error(t("planning.comments.create_error", "No se pudo crear."));
    } finally {
      setCreating(false);
    }
  };

  // -------- vista de un thread individual --------
  if (openTicketId != null) {
    return (
      <div>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => setOpenTicketId(null)}
          style={{ paddingLeft: 0, marginBottom: 12 }}
        >
          {t("planning.comments.back_to_list", "Volver a la lista de hilos")}
        </Button>
        <TicketThread
          ticketId={openTicketId}
          onChanged={notifyPanelReload}
          emptyDescription={t(
            "planning.comments.empty_thread",
            "Aún no hay respuestas en este hilo.",
          )}
        />
      </div>
    );
  }

  // -------- vista lista de threads --------
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Space>
          <Text type="secondary">
            {t(
              "planning.comments.header_hint",
              "Cada hilo es una conversación con su propio asunto. Los participantes reciben email por cada respuesta.",
            )}
          </Text>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void reload()}
            loading={loading}
          >
            {t("common.reload", "Recargar")}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setNewModalOpen(true)}
          >
            {t("planning.comments.new_thread", "Nuevo hilo")}
          </Button>
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : threads.length === 0 ? (
        <Empty
          description={t(
            "planning.comments.empty_list",
            "Sin hilos todavía. Crea el primero desde 'Nuevo hilo'.",
          )}
        />
      ) : (
        <List
          bordered
          dataSource={threads}
          rowKey="id"
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="open"
                  type="link"
                  icon={<CommentOutlined />}
                  onClick={() => setOpenTicketId(item.id)}
                >
                  {t("common.open", "Abrir")}
                </Button>,
              ]}
              style={{ cursor: "pointer" }}
              onClick={() => setOpenTicketId(item.id)}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <strong>{item.subject}</strong>
                    {item.unread_count > 0 && (
                      <Badge
                        count={item.unread_count}
                        style={{ backgroundColor: "#faad14" }}
                      />
                    )}
                    <Tag color="default">{item.code}</Tag>
                    {item.status !== 1 && (
                      <Tag
                        color={item.status === 2 ? "green" : "default"}
                      >
                        {item.status_display}
                      </Tag>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <div
                      style={{
                        color: "#8c8c8c",
                        marginBottom: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {item.last_message_excerpt || "—"}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("planning.comments.last_activity", "Última actividad")}
                      : {dayjs(item.last_activity).format("DD/MM/YYYY HH:mm")}
                      {" · "}
                      {t("planning.comments.messages_count", {
                        n: item.message_count,
                        defaultValue: "{{n}} mensajes",
                      })}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}

      <Modal
        open={newModalOpen}
        title={t("planning.comments.new_thread", "Nuevo hilo")}
        onCancel={() => (creating ? undefined : setNewModalOpen(false))}
        onOk={onCreate}
        confirmLoading={creating}
        okText={t("planning.comments.create", "Crear")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t(
            "planning.comments.new_thread_hint",
            "El hilo se crea sobre esta planificación. Los admins, coordinadores del país y auditores asignados a la planning recibirán una notificación por email.",
          )}
        />
        <Form form={newForm} layout="vertical" preserve={false}>
          <Form.Item
            label={t("planning.comments.subject", "Asunto")}
            name="subject"
            rules={[
              {
                required: true,
                message: t(
                  "planning.comments.subject_required",
                  "Escribe un asunto",
                ),
              },
              { max: 200 },
            ]}
          >
            <Input
              placeholder={t(
                "planning.comments.subject_placeholder",
                "Ej: Nueva auditoría añadida a la planning",
              )}
              maxLength={200}
            />
          </Form.Item>
          <Form.Item
            label={t("planning.comments.body", "Comentario")}
            name="body_html"
            rules={[
              {
                required: true,
                message: t(
                  "planning.comments.body_required",
                  "Escribe el mensaje inicial",
                ),
              },
              {
                validator: (_, v) => {
                  if (!v || v === "<p><br></p>") {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: t(
                  "planning.comments.body_required",
                  "Escribe el mensaje inicial",
                ),
              },
            ]}
          >
            <RichTextEditor
              placeholder={t(
                "planning.comments.body_placeholder",
                "Escribe tu comentario…",
              )}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlanningCommentsPanel;
